import React from 'react';
import { Category, EventPageFragment, EventPageType } from '@graphql/generated';
import { Box, BoxProps, ColumnsV2, Flex, TextV2 } from '@withjoy/joykit';
import { LayoutBrannanProps } from '../../LayoutBrannan';
import { pageColumnProps, pageWidgets, PreparedPages } from './Body.constants';
import { shouldRenderGraphicAccent } from '../../../layout.utils';
import { PrettyLink } from '@apps/guest/packages/layout-engine/components/PrettyLink';
import GraphicAccent from '@apps/guest/packages/layout-engine/components/GraphicAccent';
import { InlineEditor } from '@shared/components';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { EditFontData, sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { withWindow } from '@shared/utils/withWindow';
import { routePaths, RoutesNames } from '@apps/guest/utils/RouteHelper.utils';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body4ToSubHeadingOverride, display3ToPageTitleOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { SCREENS } from '@apps/admin/routes/WebsiteDesignerV2/routes/Fonts/components/FontPack/FontPack.constants';
import { isInIframe } from '@shared/utils/isInIframe';
import { AccommodationsCountdownExperiment } from '@apps/guest/components/AccommodationsCountdownExperiment';
import { useAccommodationPageChecks } from '@apps/guest/hooks/useAccommodationPageChecks';
import { BrannanFrameMaxWidth } from '../Frames';
import { pxToRem } from '@withjoy/joykit/theme';

export interface BodyProps extends Readonly<{ page: Maybe<EventPageFragment>; photo?: React.ReactNode } & LayoutBrannanProps & BoxProps> {}

const Body: React.FC<BodyProps> = React.memo(({ isSinglePage, page, event, eventHandle, photo, ...restProps }) => {
  /**
   * Determines whether the current page is the "accommodations" page.
   *
   * Previously, this logic relied on detecting the active route to adjust spacing and alignment
   * specifically for the accommodations page. However, with the new "Simple Scroll" layout —
   * where all pages are rendered at once — this approach is no longer sufficient.
   *
   * In the new layout, spacing and alignment should be applied consistently, regardless of routing.
   * Therefore, `isAccommodations` is now determined using `page.pageSlug`,
   * which uniquely identifies the accommodations page for new "Simple Scroll" layout.
   */
  const { isAccommodationsPage, isNativeAccommodationPage } = useAccommodationPageChecks(page?.pageSlug);

  const isPreviewing = isInIframe();

  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } }, false);
  const handleEditTextClick = useEventCallback((textContent: string, label: string, pageId: string) => {
    // send the message to the parent window to open the text editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editTextArea', inlineEditData: { textContent, label, pageId, validationConstraints: { maxLength: 140 } } },
    });
  });

  const onInlineEditClick = useEventCallback(() => {
    if (page) {
      sendMessageToParentWindow({
        action: 'inlineEditingInteraction',
        source: 'joyPreview',
        value: {
          action: 'editPageNote',
          inlineEditData: {
            pageId: page.id,
            isPageContainer: page?.isPageContainer || false,
            pageSubTitle: page?.subTitle || '',
          },
        },
      });
    }
  });

  const onPageEditClick = useEventCallback(() => {
    const path = page?.type === EventPageType.custom && page.pageSlug === 'accommodations' ? 'accommodations' : page?.type;

    withWindow(window => {
      if (path && routePaths.hasOwnProperty(path)) {
        window.open(`/${eventHandle}/edit/${routePaths[path as RoutesNames].goToPath(page?.pageSlug || '')}`, '_blank');
      }
    });
  });

  const onVisibilityClick = useEventCallback(() => {
    if (page) {
      // send the message to the parent window to open the text editor
      sendMessageToParentWindow({
        action: 'inlineEditingInteraction',
        source: 'joyPreview',
        value: {
          action: 'editPageSettings',
          inlineEditData: { pageId: page.id, pageType: page.type, pageTitle: page.pageTitle, pageVisibility: page.visibility, isPageContainer: page.isPageContainer },
        },
      });
    }
  });

  const handleEditFontClick = useEventCallback((inlineEditData: EditFontData) => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editFont', inlineEditData },
    });
  });

  if (!page) {
    return null;
  }
  const { type, pageTitle, subTitle, pageSlug, id } = page;
  const graphicAccent = event.eventDesign?.accent;

  const PageWidget = pageWidgets[type as PreparedPages];
  const isWelcomePage = type === EventPageType.welcome;
  const isSchedulePage = type === EventPageType.schedule;
  const isStoryPage = type === EventPageType.story;
  const isTravelPage = type === EventPageType.travel;
  const isCustomPage = type === EventPageType.custom;
  const isAppPage = type === EventPageType.app;

  const pageContentMarkup = PageWidget && (
    <ColumnsV2.Column
      id={pageSlug}
      {...(isCustomPage && !isAccommodationsPage ? { span: [12, 12, 12] } : { ...pageColumnProps[type as PreparedPages] })}
      marginTop={isWelcomePage || isSchedulePage ? undefined : isCustomPage ? 0 : [10, null, 0]}
      {...(isTravelPage && { style: { display: 'flex', justifyContent: 'center' } })}
      // Added a maxWidth limit to single page accommodations to prevent the travel map from taking up the full screen width when a user sets a full width page photo
      {...(isAccommodationsPage && isSinglePage && { style: { maxWidth: pxToRem(BrannanFrameMaxWidth) } })}
    >
      <PageWidget
        isSinglePage={isSinglePage}
        isAccommodations={isAccommodationsPage}
        event={event}
        eventHandle={eventHandle}
        pageId={id}
        pageSlug={pageSlug}
        pageTitle={pageTitle}
        subTitle={subTitle}
      />
    </ColumnsV2.Column>
  );

  const graphicAccentMarkup = shouldRenderGraphicAccent(graphicAccent) && (
    <GraphicAccent
      id="graphic-accent"
      data-testid={'body-accent'}
      accentId={graphicAccent!}
      isInlineEditable={true}
      pageName={page.type}
      pageSlug={page.pageSlug}
    />
  );

  return (
    <ColumnsV2 {...restProps}>
      <>
        {photo}
        <ColumnsV2.Column
          style={isCustomPage ? { display: isNativeAccommodationPage && !subTitle ? 'none' : 'block' } : {}}
          span={isAccommodationsPage ? [12, 12] : [12, 10]}
          offset={isAccommodationsPage ? [0, 0] : [0, 1]}
        >
          <Flex
            flexDirection={'column'}
            alignItems={'center'}
            marginTop={9}
            marginBottom={shouldRenderGraphicAccent(graphicAccent) ? 8 : 0}
            marginX={isCustomPage ? [6, 'auto'] : [6, null, 0]}
            maxWidth={isCustomPage ? '580px' : null}
          >
            {graphicAccentMarkup}
          </Flex>
        </ColumnsV2.Column>
        <InlineEditor
          actionData={{
            edit: onPageEditClick,
            visibility: {
              function: onVisibilityClick,
              visibility: page.visibility,
            },
            font: () => handleEditFontClick({ screen: SCREENS.categoryList }),
          }}
          inset={isMobile ? { x: 8, y: -16 } : { x: 4, y: -16 }}
          wrapperType="actionOutside"
          componentName="page"
          elementLabel={page.pageTitle}
          pageName={page.type}
          pageSlug={page.pageSlug}
          stickyOnScroll={true}
          isEligibleForInlineEditing={!isWelcomePage && !isAppPage && !isStoryPage}
          wrapperCSS={{ width: '100%' }}
        >
          <ColumnsV2.Column
            style={isCustomPage ? { display: isNativeAccommodationPage && !subTitle ? 'none' : 'block', zIndex: 1 } : { zIndex: 1 }}
            span={isAccommodationsPage ? [12, 12] : [12, 10]}
            offset={isAccommodationsPage ? [0, 0] : [0, 1]}
          >
            <Flex
              flexDirection={'column'}
              alignItems={'center'}
              marginBottom={isStoryPage ? 0 : isAccommodationsPage ? { _: 6, sm2: 9 } : 9}
              marginX={isCustomPage ? [6, 'auto'] : [6, null, 0]}
              maxWidth={isCustomPage ? '580px' : null}
            >
              {isWelcomePage && pageContentMarkup}
              {!isWelcomePage && !isStoryPage && !isNativeAccommodationPage && (
                <GuestSiteTypographyOverride override={display3ToPageTitleOverride}>
                  <TextV2
                    data-testid={'body-pagetitle'}
                    typographyVariant="display3"
                    textAlign="center"
                    wordBreak="break-word"
                  >
                    {pageTitle}
                  </TextV2>
                </GuestSiteTypographyOverride>
              )}
              <PageNote
                isStoryPage={isStoryPage}
                isWelcome={isWelcomePage}
                subTitle={subTitle}
                handleEditFontClick={handleEditFontClick}
                handleEditTextClick={handleEditTextClick}
                onInlineEditClick={onInlineEditClick}
                id={id}
                page={page}
                type={type}
                pageSlug={pageSlug}
                isAccommodations={isAccommodationsPage}
                isPreviewing={isPreviewing}
              />
            </Flex>
          </ColumnsV2.Column>

          {isAccommodationsPage ? <AccommodationsCountdownExperiment /> : null}

          {!isWelcomePage && (
            <Box
              width="100%"
              paddingX={[isAccommodationsPage ? 0 : 7, null, 0]}
              display={'flex'}
              justifyContent={'center'}
            >
              {pageContentMarkup}
            </Box>
          )}
        </InlineEditor>
      </>
    </ColumnsV2>
  );
});

const PageNote = ({
  isStoryPage,
  isWelcome,
  subTitle,
  handleEditFontClick,
  handleEditTextClick,
  onInlineEditClick,
  id,
  page,
  type,
  pageSlug,
  isAccommodations,
  isPreviewing,
}: {
  isStoryPage: boolean;
  isWelcome: boolean;
  subTitle: string | null | undefined;
  handleEditFontClick: (inlineEditData: EditFontData) => void;
  handleEditTextClick: (textContent: string, label: string, pageId: string) => void;
  onInlineEditClick: () => void;
  id: string;
  page: Maybe<EventPageFragment>;
  type: EventPageType;
  pageSlug: string;
  isAccommodations: boolean;
  isPreviewing: boolean;
}) => {
  const { t } = useTranslation('joykit');
  const inlineEditorTransFn = t('inlineEditor');
  const inlineEditorTrans = t('pageNote');

  if (isStoryPage) return null;

  if (isWelcome && subTitle) {
    return (
      <Box marginTop={9}>
        <InlineEditor
          elementLabel={inlineEditorTransFn.greetings()}
          actionData={{
            edit: () => handleEditTextClick(subTitle, inlineEditorTransFn.greetings(), id),
            font: () => handleEditFontClick({ category: Category.SUB_HEADING }),
          }}
          wrapperCSS={{
            zIndex: 1200,
          }}
          componentName="greetings"
          pageName={type}
          pageSlug={pageSlug}
        >
          <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
            <TextV2
              data-testid="body-subtitle"
              tagName="div"
              typographyVariant={'body4'}
              maxWidth={500}
              textAlign="center"
            >
              <PrettyLink source={page?.pageSlug}>{subTitle}</PrettyLink>
            </TextV2>
          </GuestSiteTypographyOverride>
        </InlineEditor>
      </Box>
    );
  }

  if ((!isAccommodations && subTitle) || isAccommodations) {
    return (
      <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
        <TextV2
          data-testid="body-subtitle"
          tagName="div"
          typographyVariant={'body4'}
          marginTop={9}
          maxWidth={500}
          textAlign="center"
        >
          <InlineEditor
            actionData={{ edit: onInlineEditClick }}
            wrapperType="actionOutside"
            componentName="pageNote"
            elementLabel={inlineEditorTrans.title()}
            pageName={page?.type}
            pageSlug={pageSlug || ''}
            stickyOnScroll={true}
            wrapperCSS={{ zIndex: 30 }}
            isEligibleForInlineEditing={true}
            showIntro
          >
            {subTitle ? (
              <PrettyLink
                source={page?.pageSlug}
                {...(pageSlug === 'accommodations' ? { allowedTypes: ['EMAIL'] } : {})}
              >
                {subTitle}
              </PrettyLink>
            ) : (
              isPreviewing && (
                <TextV2
                  opacity={0.7}
                  fontStyle="italic"
                >
                  {inlineEditorTrans.defaultNote()}
                </TextV2>
              )
            )}
          </InlineEditor>
        </TextV2>
      </GuestSiteTypographyOverride>
    );
  }

  return null;
};

Body.displayName = 'Body';

export { Body };
